import { applyRemoteChanges, db, getSetting, setSetting } from './db'
import type { RemoteChange } from './types'

export type SyncState = 'offline' | 'saved' | 'syncing' | 'error'

interface ApiResult<T> {
  ok: boolean
  data?: T
  error?: string
}

function apiUrl(action: string) {
  const configured = String(import.meta.env.VITE_API_BASE || '').trim()
  const base = configured || new URL('./api/v1/index.php', window.location.href).toString()
  const url = new URL(base)
  url.searchParams.set('action', action)
  return url.toString()
}

async function request<T>(action: string, body: unknown = {}, token?: string): Promise<T> {
  const response = await fetch(apiUrl(action), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })
  const result = (await response.json()) as ApiResult<T>
  if (!response.ok || !result.ok || result.data === undefined) throw new Error(result.error || 'The server could not complete the request.')
  return result.data
}

export async function serverStatus() {
  return request<{ configured: boolean }>('status')
}

export async function login(username: string, password: string) {
  const data = await request<{ token: string; username: string }>('login', { username, password })
  await setSetting('authToken', data.token)
  await setSetting('username', data.username)
  return data
}

export async function setup(setupCode: string, username: string, password: string) {
  const data = await request<{ token: string; username: string }>('setup', { setupCode, username, password })
  await setSetting('authToken', data.token)
  await setSetting('username', data.username)
  return data
}

export async function logout() {
  const token = await getSetting('authToken', '')
  if (token) await request<{ loggedOut: boolean }>('logout', {}, token).catch(() => undefined)
  await setSetting('authToken', '')
  await setSetting('username', '')
}

export async function syncNow(): Promise<boolean> {
  const token = await getSetting('authToken', '')
  if (!token || !navigator.onLine) return false

  const since = await getSetting('syncCursor', 0)
  const items = await db.outbox.orderBy('createdAt').toArray()
  const data = await request<{ cursor: number; changes: RemoteChange[] }>(
    'sync',
    {
      since,
      changes: items.map((item) => ({ entity: item.entity, payload: item.payload })),
    },
    token,
  )
  await applyRemoteChanges(data.changes)
  const ids = items.flatMap((item) => (item.id === undefined ? [] : [item.id]))
  if (ids.length) await db.outbox.bulkDelete(ids)
  await setSetting('syncCursor', data.cursor)
  return true
}

